/**
 * Listing Card Component
 *
 * Visual-first card design for boat listings
 * Supports both grid and list view modes
 */

import { Link } from "react-router-dom";
import { Listing } from "@/lib/api";

interface Props {
  listing: Listing;
  viewMode?: "grid" | "list";
}

export function ListingCard({ listing, viewMode = "grid" }: Props) {
  const imageUrl = listing.mediaUrls[0] || "https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=800&q=80";

  // Format price with proper currency symbol
  const formatPrice = (price: number, currency: string) => {
    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return formatter.format(price / 100);
  };

  const tierColors = {
    featured: "bg-gradient-to-r from-blue-500 to-cyan-500",
    top: "bg-gradient-to-r from-amber-500 to-orange-500",
    base: "bg-slate-600",
  };

  const tierBg = tierColors[listing.packageTier?.toLowerCase() as keyof typeof tierColors] || tierColors.base;

  if (viewMode === "list") {
    return (
      <Link to={`/listing/${listing.id}`} className="block group">
        <div className="card hover:shadow-2xl transition-all duration-300 overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-3 h-full">
            {/* Image */}
            <div className="relative h-56 md:h-64 overflow-hidden">
              <img
                src={imageUrl}
                alt={listing.title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              {/* Tier Badge */}
              {listing.packageTier && (
                <div className={`absolute top-4 left-4 ${tierBg} text-white px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide shadow-lg`}>
                  {listing.packageTier}
                </div>
              )}
              {/* Save Button */}
              <button
                className="absolute top-4 right-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white hover:scale-110 transition-all"
                onClick={(e) => { e.preventDefault(); }}
              >
                <svg className="w-5 h-5 text-slate-600 hover:text-red-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="md:col-span-2 p-6 flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-xl font-bold text-ink group-hover:text-accent transition-colors line-clamp-1">
                      {listing.title}
                    </h3>
                    <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {listing.location}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-accent">
                      {formatPrice(listing.price, listing.currency)}
                    </p>
                  </div>
                </div>

                {/* Specs Pills */}
                <div className="flex flex-wrap gap-2 my-4">
                  <span className="px-3 py-1 bg-slate-100 rounded-full text-xs font-medium text-slate-700 capitalize">
                    {listing.type}
                  </span>
                  <span className="px-3 py-1 bg-slate-100 rounded-full text-xs font-medium text-slate-700">
                    {listing.length} {listing.lengthUnit}
                  </span>
                  <span className="px-3 py-1 bg-slate-100 rounded-full text-xs font-medium text-slate-700">
                    {listing.year}
                  </span>
                  <span className="px-3 py-1 bg-slate-100 rounded-full text-xs font-medium text-slate-700">
                    {listing.make}
                  </span>
                </div>

                <p className="text-slate-600 text-sm line-clamp-2">
                  {listing.description}
                </p>
              </div>

              {/* Footer Stats */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-ash text-sm text-slate-500">
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    {listing.views || 0}
                  </span>
                  <span className="flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    {listing.inquiries || 0}
                  </span>
                </div>
                <span className="text-accent font-semibold group-hover:underline">
                  View Details →
                </span>
              </div>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  // Grid view (default) - Visual-first card
  return (
    <Link to={`/listing/${listing.id}`} className="block group">
      <div className="card hover:shadow-2xl transition-all duration-300 overflow-hidden h-full flex flex-col">
        {/* Hero Image */}
        <div className="relative h-56 overflow-hidden">
          <img
            src={imageUrl}
            alt={listing.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />

          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

          {/* Tier Badge */}
          {listing.packageTier && (
            <div className={`absolute top-4 left-4 ${tierBg} text-white px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide shadow-lg`}>
              {listing.packageTier}
            </div>
          )}

          {/* Save Button */}
          <button
            className="absolute top-4 right-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white hover:scale-110 transition-all opacity-0 group-hover:opacity-100"
            onClick={(e) => { e.preventDefault(); }}
          >
            <svg className="w-5 h-5 text-slate-600 hover:text-red-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </button>

          {/* Price Overlay */}
          <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
            <div>
              <p className="text-white/80 text-xs font-medium flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                </svg>
                {listing.location}
              </p>
            </div>
            <div className="bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow-lg">
              <p className="text-lg font-bold text-ink">
                {formatPrice(listing.price, listing.currency)}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 flex-1 flex flex-col">
          <h3 className="text-lg font-bold text-ink group-hover:text-accent transition-colors line-clamp-1 mb-2">
            {listing.title}
          </h3>

          {/* Quick Specs */}
          <div className="flex flex-wrap gap-2 mb-3">
            <span className="px-2 py-0.5 bg-slate-100 rounded text-xs font-medium text-slate-600 capitalize">
              {listing.type}
            </span>
            <span className="px-2 py-0.5 bg-slate-100 rounded text-xs font-medium text-slate-600">
              {listing.length} {listing.lengthUnit}
            </span>
            <span className="px-2 py-0.5 bg-slate-100 rounded text-xs font-medium text-slate-600">
              {listing.year}
            </span>
          </div>

          <p className="text-slate-600 text-sm line-clamp-2 flex-1">
            {listing.description}
          </p>

          {/* Footer */}
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-ash/50 text-xs text-slate-500">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                {listing.views || 0}
              </span>
              <span className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                {listing.inquiries || 0}
              </span>
            </div>
            <span className="text-accent font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
              View →
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
